// Which section's fly-out is showing beside the nav panel, and the pointer
// bookkeeping that goes with it.
//
// Above mobile a section row no longer pushes its pages into the column, it
// opens them alongside. That makes the open section panel state rather than row
// state: only one may be open, and the icon rail and the labelled band share
// it. The row that opened it is kept too, because it is both what the fly-out
// positions against and what focus goes back to.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SectionId } from './config';

/** Grace period for a pointer crossing the gap between row and fly-out. */
const HOVER_GRACE_MS = 160;

interface OpenFlyout {
  id: SectionId;
  anchor: HTMLElement;
  /** The fly-out takes focus only when the keyboard opened it. */
  autoFocus: boolean;
  /** Opened by hovering rather than by pressing the row. */
  viaHover: boolean;
}

export interface NavFlyoutApi {
  open: OpenFlyout | null;
  show: (id: SectionId, anchor: HTMLElement, autoFocus: boolean) => void;
  toggle: (id: SectionId, anchor: HTMLElement, autoFocus: boolean) => void;
  close: (restoreFocus?: boolean) => void;
  /** The pointer reached somewhere that counts — cancel a pending close. */
  keepOpen: () => void;
  /** Start a close, so crossing the gap to the fly-out does not dismiss it. */
  scheduleClose: () => void;
  /** The fly-out reports its own element so an outside press can spare it. */
  setSurface: (el: HTMLElement | null) => void;
}

export function useNavFlyout(): NavFlyoutApi {
  const [open, setOpen] = useState<OpenFlyout | null>(null);
  const surface = useRef<HTMLElement | null>(null);
  const timer = useRef<number | null>(null);
  // Read by handlers bound once, which must not close over a stale value.
  const openRef = useRef<OpenFlyout | null>(null);
  openRef.current = open;

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const close = useCallback(
    (restoreFocus = false) => {
      clearTimer();
      const previous = openRef.current;
      setOpen(null);
      if (restoreFocus) previous?.anchor.focus();
    },
    [clearTimer],
  );

  const show = useCallback(
    (id: SectionId, anchor: HTMLElement, autoFocus: boolean) => {
      clearTimer();
      setOpen({ id, anchor, autoFocus, viaHover: true });
    },
    [clearTimer],
  );

  const toggle = useCallback(
    (id: SectionId, anchor: HTMLElement, autoFocus: boolean) => {
      const current = openRef.current;
      // Pressing a row a hover already opened means "open", not "close" — on a
      // mouse the pointer is always on the row it is about to click, so a plain
      // toggle would shut the fly-out the hover had just brought up. Pressing
      // it a second time, with the open now owned by the press, closes.
      if (current?.id === id && !current.viaHover) {
        close(autoFocus);
        return;
      }
      clearTimer();
      setOpen({ id, anchor, autoFocus, viaHover: false });
    },
    [clearTimer, close],
  );

  const keepOpen = useCallback(() => clearTimer(), [clearTimer]);

  const scheduleClose = useCallback(() => {
    clearTimer();
    timer.current = window.setTimeout(() => close(), HOVER_GRACE_MS);
  }, [clearTimer, close]);

  const setSurface = useCallback((el: HTMLElement | null) => {
    surface.current = el;
  }, []);

  // A press that is neither the fly-out nor the row that opened it dismisses.
  // This is also what closes it on a touch outside the nav, where there is no
  // pointer to leave.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (surface.current?.contains(target)) return;
      if (open.anchor.contains(target)) return;
      close();
    };
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  useEffect(() => clearTimer, [clearTimer]);

  return { open, show, toggle, close, keepOpen, scheduleClose, setSurface };
}
